/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery } from 'react-query'
import { useMemo } from 'react'

import {
  getElasticsearchProject,
  getElasticsearchProjectRoles,
  getObservabilityProject,
  getObservabilityProjectRoles,
  getSecurityProject,
  getSecurityProjectRoles,
} from '@modules/project-user-api/v1/callers'
import type { ProjectId } from '@modules/project-user-api/v1/types'
import type { QueryHook, QueryHookOptions } from '@modules/query/types'
import type { ProjectApiError, ProjectType } from '@modules/ui-types/projects'
import {
  getShouldUsePrivateEndpoints,
  useProjectHasTrafficFilters,
} from '@modules/kibana-redirect/hooks/useProjectHasTrafficFilters'
import { AnyUserProject } from '@modules/project-user-api/types'

import { makeQueryKey } from './helpers'

function makeOnwardOptions(type: ProjectType, projectType: ProjectType, options: QueryHookOptions) {
  const consumerEnabled = options.enabled ?? true // true being the useQuery default
  return {
    ...options,
    enabled: type === projectType && consumerEnabled,
  }
}

/**
 * A convenience wrapper hook to get a single project.
 */
export function useGetProjectQuery(
  type: ProjectType,
  id: ProjectId,
  options: QueryHookOptions = {},
) {
  const elasticsearchQuery = useGetElasticsearchProjectQuery(
    id,
    makeOnwardOptions(type, 'elasticsearch', options),
  )
  const observabilityQuery = useGetObservabilityProjectQuery(
    id,
    makeOnwardOptions(type, 'observability', options),
  )
  const securityQuery = useGetSecurityProjectQuery(id, makeOnwardOptions(type, 'security', options))

  const queryResult = {
    elasticsearch: elasticsearchQuery,
    observability: observabilityQuery,
    security: securityQuery,
  }[type]

  // Enrich project with traffic filter metadata, don't block the loading of the project since
  // we need to perform multiple queries to get the traffic filter metadata
  const { hasPrivateLink, hasIpFilter, isLoading, isFetching } = useProjectHasTrafficFilters(
    queryResult.data,
  )

  const projectWithTrafficFilterMetadata: AnyUserProject | undefined = useMemo(() => {
    if (!queryResult.data) {
      return undefined
    }

    return {
      ...queryResult.data,
      hasPrivateLink,
      hasIpFilter,
      usePrivateEndpoints: getShouldUsePrivateEndpoints({ hasPrivateLink, hasIpFilter }),
    }
  }, [queryResult.data, hasPrivateLink, hasIpFilter])

  return {
    ...queryResult,
    isLoading: queryResult.isLoading || isLoading,
    isFetching: queryResult.isFetching || isFetching,
    data: projectWithTrafficFilterMetadata,
  }
}

const useGetElasticsearchProjectQuery: QueryHook<
  typeof getElasticsearchProject,
  ProjectId,
  ProjectApiError
> = (id: ProjectId, options: QueryHookOptions) =>
  useQuery(
    makeQueryKey('getElasticsearchProject', id),
    () =>
      getElasticsearchProject({
        pathParameters: { id },
      }),
    options,
  )

const useGetObservabilityProjectQuery: QueryHook<
  typeof getObservabilityProject,
  ProjectId,
  ProjectApiError
> = (id: ProjectId, options: QueryHookOptions) =>
  useQuery(
    makeQueryKey('getObservabilityProject', id),
    () =>
      getObservabilityProject({
        pathParameters: { id },
      }),
    options,
  )

const useGetSecurityProjectQuery: QueryHook<
  typeof getSecurityProject,
  ProjectId,
  ProjectApiError
> = (id: ProjectId, options: QueryHookOptions) =>
  useQuery(
    makeQueryKey('getSecurityProject', id),
    () =>
      getSecurityProject({
        pathParameters: { id },
      }),
    options,
  )

export const useGetElasticsearchProjectRolesQuery: QueryHook<
  typeof getElasticsearchProjectRoles
> = (id: ProjectId, options?: { enabled?: boolean }) =>
  useQuery(
    makeQueryKey('getElasticsearchProjectRoles', id),
    () =>
      getElasticsearchProjectRoles({
        pathParameters: { id },
      }),
    options,
  )

// ToDo: remove disable below when custom roles are enabled Observability
// and this export gets used
// ts-unused-exports:disable-next-line
export const useGetObservabilityProjectRolesQuery: QueryHook<
  typeof getObservabilityProjectRoles
> = (id: ProjectId, options?: { enabled?: boolean }) =>
  useQuery(
    makeQueryKey('getObservabilityProjectRoles', id),
    () =>
      getObservabilityProjectRoles({
        pathParameters: { id },
      }),
    { enabled: options?.enabled ?? true },
  )

export const useGetSecurityProjectRolesQuery: QueryHook<typeof getSecurityProjectRoles> = (
  id: ProjectId,
  options?: { enabled?: boolean },
) =>
  useQuery(
    makeQueryKey('getSecurityProjectRoles', id),
    () =>
      getSecurityProjectRoles({
        pathParameters: { id },
      }),
    { enabled: options?.enabled ?? true },
  )
